// Inheritance between an installation's override and the org default. `merge` mirrors the Go merge
// (api/internal/domain/settings): objects deep-merge, arrays and scalars (null included) replace.
export type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
export type JsonObject = { [key: string]: Json };

export function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function merge(base: Json | undefined, override: Json | undefined): Json | undefined {
  if (override === undefined) return base;
  if (isObject(base) && isObject(override)) {
    const out: JsonObject = { ...base };
    for (const [key, value] of Object.entries(override)) {
      const merged = merge(base[key], value);
      if (merged !== undefined) out[key] = merged;
    }
    return out;
  }
  return override;
}

/** The value at a dotted path, or undefined. */
export function getPath(document: Json | undefined, path: string): Json | undefined {
  let node: Json | undefined = document;
  for (const part of path.split(".")) {
    if (!isObject(node)) return undefined;
    node = node[part];
  }
  return node;
}

/** A copy of the document with the value at the dotted path set (undefined removes it). */
export function setPath(document: JsonObject, path: string, value: Json | undefined): JsonObject {
  const [head, ...rest] = path.split(".") as [string, ...string[]];
  const out: JsonObject = { ...document };
  if (rest.length === 0) {
    if (value === undefined) delete out[head];
    else out[head] = value;
    return out;
  }
  const child = out[head];
  out[head] = setPath(isObject(child) ? child : {}, rest.join("."), value);
  return out;
}

/** True when the override does not set the path, so the value shown is the org default's. */
export function isInherited(override: Json | undefined, path: string): boolean {
  return getPath(override, path) === undefined;
}
